// @ts-nocheck
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, ChevronLeft, Trophy, Calendar, Users } from "lucide-react";

export default function CircuitDetailPage() {
  const { circuitId } = useParams();

  const { data: circuit, isLoading } = useQuery({
    queryKey: ["circuit-detail", circuitId],
    enabled: !!circuitId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("circuits")
        .select("*")
        .eq("id", circuitId)
        .maybeSingle();
      return data;
    },
  });

  const { data: events = [] } = useQuery({
    queryKey: ["circuit-events", circuitId],
    enabled: !!circuitId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("circuit_event_links")
        .select("*, events(id,name,start_date,slug,status)")
        .eq("circuit_id", circuitId);
      return data || [];
    },
  });

  const { data: standings = [] } = useQuery({
    queryKey: ["circuit-standings", circuitId],
    enabled: !!circuitId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("circuit_ranking_points")
        .select("team_id, team_name, points_awarded, result")
        .eq("circuit_id", circuitId);
      const map = new Map<string, any>();
      (data || []).forEach((r: any) => {
        const key = r.team_id || r.team_name;
        const cur = map.get(key) || {
          team_name: r.team_name,
          team_id: r.team_id,
          points: 0,
          wins: 0,
          losses: 0,
        };
        cur.points += Number(r.points_awarded || 0);
        if (r.result === "win") cur.wins += 1;
        if (r.result === "loss") cur.losses += 1;
        map.set(key, cur);
      });
      return Array.from(map.values()).sort((a, b) => b.points - a.points);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!circuit) {
    return <div className="p-6">Circuit not found.</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <Link
        to="/admin-dashboard/circuits"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="w-4 h-4" /> Back to circuits
      </Link>

      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Trophy className="w-6 h-6 text-primary" /> {circuit.name}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {[circuit.circuit_type, circuit.age_group, circuit.gender, circuit.season]
            .filter(Boolean)
            .join(" · ")}
        </p>
        {circuit.description && (
          <p className="text-sm mt-3 max-w-2xl">{circuit.description}</p>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="border rounded-lg bg-card">
          <div className="p-4 border-b font-semibold flex items-center gap-2">
            <Calendar className="w-4 h-4" /> Linked Events ({events.length})
          </div>
          {events.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">
              No events linked yet. Open an event editor and toggle the Circuit step.
            </div>
          ) : (
            <ul className="divide-y">
              {events.map((l: any) => (
                <li key={l.id} className="p-4">
                  <Link
                    to={`/admin-dashboard/events/${l.event_id}/edit`}
                    className="font-medium hover:underline"
                  >
                    {l.events?.name || "Event"}
                  </Link>
                  <div className="text-xs text-muted-foreground mt-1">
                    {l.events?.start_date} · multiplier {l.point_multiplier}x
                    {l.is_live_period && " · LIVE PERIOD"}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border rounded-lg bg-card">
          <div className="p-4 border-b font-semibold flex items-center gap-2">
            <Users className="w-4 h-4" /> Standings ({standings.length})
          </div>
          {standings.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">
              No ranking points awarded yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">#</th>
                  <th className="px-4 py-2 text-left font-medium">Team</th>
                  <th className="px-4 py-2 text-right font-medium">W-L</th>
                  <th className="px-4 py-2 text-right font-medium">Points</th>
                </tr>
              </thead>
              <tbody>
                {standings.map((s: any, i: number) => (
                  <tr key={s.team_id || s.team_name} className="border-t">
                    <td className="px-4 py-2">{i + 1}</td>
                    <td className="px-4 py-2 font-medium">{s.team_name}</td>
                    <td className="px-4 py-2 text-right text-muted-foreground">
                      {s.wins}-{s.losses}
                    </td>
                    <td className="px-4 py-2 text-right font-bold">
                      {s.points.toFixed(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
